import { lazy } from 'react';
import type { ComponentType } from 'react';

/**
 * Wraps React.lazy() with automatic retry logic for chunk loading failures.
 *
 * When a chunk fails to load (typically after a deployment when old chunks are removed),
 * this will automatically reload the page once to fetch the new index.html with updated
 * chunk references.
 *
 * Uses sessionStorage to track if a reload has already happened to prevent infinite loops.
 *
 * @param importFn - The dynamic import function (e.g., () => import('./pages/Dashboard'))
 * @returns A lazy-loaded React component with retry logic
 *
 * @example
 * const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'));
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
) {
  return lazy(() =>
    importFn().catch((error) => {
      // Check if this is a chunk loading error
      const isChunkError =
        error?.name === 'ChunkLoadError' ||
        error?.message?.includes('Failed to fetch dynamically imported module') ||
        error?.message?.includes('Importing a module script failed');

      if (!isChunkError) {
        // Not a chunk error, throw it normally
        throw error;
      }

      // Chunk failed to load, likely a new deployment
      const RELOAD_KEY = 'chunk-reload';
      const hasReloaded = sessionStorage.getItem(RELOAD_KEY);

      if (!hasReloaded) {
        // First time encountering this error, reload the page
        sessionStorage.setItem(RELOAD_KEY, 'true');
        window.location.reload();
        // Return a promise that never resolves since we're reloading
        return new Promise(() => {});
      }

      // Already reloaded once, clear the flag and throw the error
      sessionStorage.removeItem(RELOAD_KEY);
      throw new Error('Failed to load page after retry. Please clear your browser cache and try again.');
    })
  );
}
